import { useState } from 'react';
import type { Expense, ExpenseCategory, PaidBy } from '../types';
import { EXPENSE_CATEGORY_LABELS, EXPENSE_CATEGORY_ORDER, PAID_BY_LABELS } from '../types';
import * as db from '../lib/db';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function ExpenseForm({
  propertyId,
  existing,
  onDone,
  onCancel,
}: {
  propertyId: string;
  existing?: Expense;
  onDone: (expense: Expense) => void;
  onCancel: () => void;
}) {
  const [expenseDate, setExpenseDate] = useState(existing?.expense_date ?? todayISO());
  const [amount, setAmount] = useState(existing?.amount?.toString() ?? '');
  const [category, setCategory] = useState<ExpenseCategory>(existing?.category ?? 'other');
  const [payee, setPayee] = useState(existing?.payee ?? '');
  const [paidBy, setPaidBy] = useState<PaidBy>(existing?.paid_by ?? 'joint');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!amount || !expenseDate) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        property_id: propertyId,
        expense_date: expenseDate,
        amount: Number(amount),
        category,
        payee: payee.trim() || null,
        paid_by: paidBy,
        notes: notes.trim() || null,
      };
      const expense = existing
        ? await db.updateExpense(existing.id, payload)
        : await db.createExpense(payload);
      onDone(expense);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="form card" onSubmit={handleSubmit}>
      <h2>{existing ? 'Edit expense' : 'Add an expense'}</h2>

      <label>
        Date
        <input
          type="date"
          value={expenseDate}
          onChange={(e) => setExpenseDate(e.target.value)}
          required
        />
      </label>

      <label>
        Amount
        <input
          type="number"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          required
          autoFocus
        />
      </label>

      <label>
        Category
        <select value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
          {EXPENSE_CATEGORY_ORDER.map((cat) => (
            <option key={cat} value={cat}>
              {EXPENSE_CATEGORY_LABELS[cat]}
            </option>
          ))}
        </select>
      </label>

      <label>
        Payee / vendor (optional)
        <input value={payee} onChange={(e) => setPayee(e.target.value)} />
      </label>

      <label>
        Paid by
        <select value={paidBy} onChange={(e) => setPaidBy(e.target.value as PaidBy)}>
          {(Object.keys(PAID_BY_LABELS) as PaidBy[]).map((p) => (
            <option key={p} value={p}>
              {PAID_BY_LABELS[p]}
            </option>
          ))}
        </select>
      </label>

      <label>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
